import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { DemoLeadModal } from '@/components/DemoLead/DemoLeadModal';
import {
  clearDemoLead,
  loadDemoLead,
  setRedirectDemoRequestId,
} from '@/components/DemoLead/demoLeadStorage';
import { useLaunchDemo } from '@/components/DemoLead/useLaunchDemo';
import config from '@/config';
import { isWebsiteDemoPractice } from '@/util/demoPractice';
import { RootState } from '@/types/graph';

/**
 * Reads the entry URL once, at mount. `demo_lead=<id>` marks the redirect
 * that follows the lead-form submission (redux searchParams is seeded from
 * the same window.location.search, so a second read would always agree).
 * Any other fresh load of the demo-practice entry URL restarts the demo.
 */
const readDemoEntry = () => {
  const redirectDemoRequestId =
    new URLSearchParams(window.location.search).get('demo_lead') || null;
  // Trailing slashes are stripped: the router matches `/<slug>/` too
  const pathname = window.location.pathname.replace(/\/+$/, '');
  const isDemoEntryUrl = pathname === `/${config.demoPracticeSlug}`;
  const isRestart = isDemoEntryUrl && !redirectDemoRequestId;
  return {
    storedLead: isRestart ? null : loadDemoLead(),
    redirectDemoRequestId,
    isRestart,
  };
};

/**
 * Entry point for the website demo flow: shows the blocking lead-capture
 * modal whenever the demo practice is entered. Every entry restarts the demo
 * a DemoRequest's questionnaire is never resumed
 * nor overwritten.
 */
export const DemoLeadModalGate = () => {
  const practiceSlug = useSelector(
    (state: RootState) => state.consultation.practice?.slug,
  );
  const invitationId = useSelector((state: RootState) => state.invitationId);
  // queryVariant (unlike referrer) reflects the current URL only, already
  // normalized (dash → underscore) by App.tsx
  const queryVariant = useSelector((state: RootState) => state.queryVariant);
  const launchDemo = useLaunchDemo();
  // Synchronous invitation-entry check: the invitationId guard below only
  // kicks in after EntryRouter's fetch, too late to prevent a modal flash on
  // a patient opening an invitation link on the demo practice
  const [isInvitationEntry] = useState(
    () =>
      window.location.pathname.startsWith('/invitation/') ||
      window.location.pathname.startsWith('/i/'),
  );
  const [{ storedLead, redirectDemoRequestId, isRestart }] =
    useState(readDemoEntry);

  useEffect(() => {
    if (isRestart) clearDemoLead();
  }, [isRestart]);

  // Keeps the questionnaire linkable to the lead for the rest of this page
  // load even when sessionStorage refused to hold it
  useEffect(() => {
    if (redirectDemoRequestId) setRedirectDemoRequestId(redirectDemoRequestId);
  }, [redirectDemoRequestId]);

  const showModal =
    isWebsiteDemoPractice(practiceSlug) &&
    !storedLead &&
    !redirectDemoRequestId;
  // Never block an invitation flow, even one issued from the demo practice
  if (!showModal || invitationId || isInvitationEntry) return null;

  return (
    <DemoLeadModal defaultVariantSlug={queryVariant} onSubmit={launchDemo} />
  );
};
